import React from 'react';
import { Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import CurrencyInput from '../../common/CurrencyInput';
import { getInvoiceBalanceDue } from '../../reports/utils/helpers';
import { formatCurrency } from '../../../helpers/global';
import { Check, CircleAlert, CircleCheck, DollarSign } from 'lucide-react';

const STATUS_OPTIONS = [
  { label: 'Unpaid', icon: CircleAlert },
  { label: 'Paid', icon: CircleCheck }
];

const INVOICE_COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice number', type: 'text', width: 120 },
  { key: 'invoiceAmount', label: 'Invoice amount', type: 'currency', align: 'right', width: 120 },
  { key: 'paymentReceived', label: 'Payment received', type: 'currency', align: 'right', width: 120 },
  { key: 'balanceDue', label: 'Balance due', type: 'static', align: 'right', width: 100 },
  { key: 'status', label: 'Status', type: 'status', width: 200 }
];

const toCurrencyValue = value => (value === '' || value == null ? '' : Number(value));

const computeTotals = invoices =>
  invoices.reduce(
    (acc, row) => {
      const amount = toCurrencyValue(row.invoiceAmount) || 0;
      const received = toCurrencyValue(row.paymentReceived) || 0;
      const balance = Math.max(amount - received, 0);
      acc.amount += amount;
      acc.received += received;
      acc.balance += balance;
      if (balance <= 0) acc.settled += 1;
      return acc;
    },
    { amount: 0, received: 0, balance: 0, settled: 0 }
  );

const thClassName = (column, index) =>
  ['align-middle text-nowrap text-uppercase font-weight-medium text-muted', column.align === 'right' && 'text-right']
    .filter(Boolean)
    .join(' ');

const tdClassName = (column, extra = '') =>
  ['align-middle', column.align === 'right' && 'text-right', extra].filter(Boolean).join(' ');

const cellStyle = column => ({
  width: column.width,
  minWidth: column.width,
  maxWidth: column.width,
  padding: '8px'
});

const FinancialCloseoutInvoiceTable = ({ invoices, loading = false, disabled, onFieldChange, onStatusChange }) => {
  const totals = computeTotals(invoices);
  const allSettled = invoices.length > 0 && totals.settled === invoices.length;

  const renderCell = (column, row, index) => {
    const balanceDue = getInvoiceBalanceDue(row);
    const settled = balanceDue <= 0;

    if (column.type === 'text') {
      return (
        <>
          <span
            aria-hidden
            className={`position-absolute rounded-pill ${settled ? 'bg-success' : 'bg-danger'}`}
            style={{ left: 0, top: 6, bottom: 6, width: 3 }}
          />
          <Form.Control
            type="text"
            value={row[column.key] ?? ''}
            onChange={e => onFieldChange(index, column.key, e.target.value)}
            disabled={disabled}
            className="midFont bg-white"
            style={{ height: 30 }}
          />
        </>
      );
    }

    if (column.type === 'currency') {
      return (
        <div
          className="d-flex align-items-center bg-white border"
          style={{ borderRadius: 10, padding: '0px 6px', height: 30 }}
        >
          <span
            className="text-muted midFont mr-1"
            style={{ lineHeight: 1, display: 'flex', alignItems: 'center', gap: 2 }}
          >
            <DollarSign size={11} aria-hidden />
          </span>
          <CurrencyInput
            value={toCurrencyValue(row[column.key])}
            onChange={value => onFieldChange(index, column.key, value)}
            disabled={disabled}
            height="28px"
            className="text-right midFont w-100 border-0 shadow-none bg-transparent"
            inputStyle={{
              padding: 0,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              backgroundColor: 'transparent',
              boxShadow: 'none'
            }}
          />
        </div>
      );
    }

    if (column.type === 'static') {
      const display = settled ? '—' : formatCurrency(balanceDue);
      const content = (
        <span
          className={`d-block text-truncate font-weight-bold ${settled ? 'text-success' : 'text-danger'}`}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {display}
        </span>
      );

      if (settled) return content;

      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`invoice-balance-tooltip-${row._id || index}`} style={{ zIndex: 100000 }}>
              {display}
            </Tooltip>
          }
        >
          {content}
        </OverlayTrigger>
      );
    }

    if (column.type === 'status') {
      const handleStatusSelect = option => {
        if (disabled || (option === 'Paid' && !settled)) return;
        onStatusChange(index, option);
      };

      return (
        <div
          role="radiogroup"
          aria-label="Invoice status"
          className="d-inline-flex align-items-center w-100 bg-muted border rounded-pill"
          style={{ maxWidth: 200, padding: 3 }}
        >
          {STATUS_OPTIONS.map(({ label: option, icon: Icon }) => {
            const active = row.status === option;
            const paidBlocked = option === 'Paid' && !settled;
            const pillDisabled = disabled || (paidBlocked && !active);

            const pill = (
              <div
                role="radio"
                aria-checked={active}
                tabIndex={pillDisabled ? -1 : 0}
                onClick={() => handleStatusSelect(option)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStatusSelect(option);
                  }
                }}
                className={`d-inline-flex w-100 align-items-center justify-content-center font-weight-bold xsmallFont py-1 px-0 rounded-pill
                  ${active && option === 'Paid' ? 'bg-success text-white' : ''}
                  ${active && option !== 'Paid' ? 'bg-white text-danger' : ''}
                  ${!active ? 'text-muted' : ''}`}
                style={{
                  gap: 4,
                  minHeight: 24,
                  opacity: paidBlocked && !active ? 0.65 : 1,
                  cursor: pillDisabled ? 'not-allowed' : 'pointer',
                  boxShadow: active ? (option === 'Paid' ? '0 1px 2px #00000014' : '0 0 0 1px #dc354540') : 'none'
                }}
              >
                <Icon size={12} aria-hidden style={{ flexShrink: 0 }} />
                <span style={{ pointerEvents: 'none', color: 'inherit' }}>{option}</span>
              </div>
            );

            if (paidBlocked) {
              return (
                <OverlayTrigger
                  key={option}
                  placement="top"
                  overlay={
                    <Tooltip id={`invoice-paid-tooltip-${row._id || index}`} style={{ zIndex: 100000 }}>
                      There must be no balance due before marking this invoice as Paid.
                    </Tooltip>
                  }
                >
                  {pill}
                </OverlayTrigger>
              );
            }

            return <React.Fragment key={option}>{pill}</React.Fragment>;
          })}
        </div>
      );
    }

    return null;
  };

  const renderFooterCell = column => {
    if (column.key === 'invoiceNumber') return 'Totals';
    if (column.key === 'status') return null;

    let display = null;
    if (column.key === 'invoiceAmount') display = formatCurrency(totals.amount);
    else if (column.key === 'paymentReceived') display = formatCurrency(totals.received);
    else if (column.key === 'balanceDue') {
      display = totals.balance > 0 ? formatCurrency(totals.balance) : '—';
    }
    if (display == null) return null;

    const content = (
      <span className="d-block text-truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {display}
      </span>
    );
    if (display === '—') return content;

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`invoice-total-tooltip-${column.key}`} style={{ zIndex: 100000 }}>
            {display}
          </Tooltip>
        }
      >
        {content}
      </OverlayTrigger>
    );
  };

  return (
    <div className="position-relative">
      <div className="mb-2 d-flex flex-wrap align-items-center justify-content-between" style={{ gap: 6 }}>
        <p className="mb-0 font-weight-bold midFont text-uppercase">Invoices</p>
        {!loading && invoices.length > 0 && (
          <span
            className={`d-inline-flex align-items-center xsmallFont font-weight-bold rounded-pill py-0 px-2 ${
              allSettled ? 'text-success' : 'text-dark'
            }`}
            style={{
              gap: 4,
              backgroundColor: '#ffc10733'
            }}
          >
            {allSettled && <Check size={12} aria-hidden />}
            {totals.settled} of {invoices.length} settled
          </span>
        )}
      </div>

      {!loading && invoices.length === 0 ? (
        <p className="text-muted midFont mb-0">No invoice rows found on this report.</p>
      ) : (
        <div className="table-responsive mb-0 border overflow-hidden" style={{ borderRadius: 8 }}>
          <Table size="sm" className="mb-0 midFont" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr className="bg-muted">
                {INVOICE_COLUMNS.map((column, index) => (
                  <th
                    key={column.key}
                    className={thClassName(column, index)}
                    style={{ ...cellStyle(column), fontSize: 11, letterSpacing: '0.03em' }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody aria-busy={loading || undefined} aria-label={loading ? 'Loading invoices' : undefined}>
              {loading
                ? Array.from({ length: 2 }, (_, rowIndex) => (
                    <tr key={`skeleton-${rowIndex}`} className="border-top">
                      {INVOICE_COLUMNS.map(column => (
                        <td key={column.key} className={tdClassName(column)} style={cellStyle(column)}>
                          <div className="shimmer shimmer-line" style={{ borderRadius: 10, height: 30 }} />
                        </td>
                      ))}
                    </tr>
                  ))
                : invoices.map((row, index) => (
                    <tr key={row._id || index} className="border-top">
                      {INVOICE_COLUMNS.map(column => (
                        <td
                          key={column.key}
                          className={tdClassName(column, column.key === 'invoiceNumber' ? 'position-relative' : '')}
                          style={cellStyle(column)}
                        >
                          {renderCell(column, row, index)}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
            {!loading && (
              <tfoot>
                <tr className="bg-muted border-top">
                  {INVOICE_COLUMNS.map(column => (
                    <td
                      key={column.key}
                      className={tdClassName(
                        column,
                        [
                          'font-weight-bold',
                          column.key === 'balanceDue' && (totals.balance > 0 ? 'text-danger' : 'text-success')
                        ]
                          .filter(Boolean)
                          .join(' ')
                      )}
                      style={cellStyle(column)}
                    >
                      {renderFooterCell(column)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
      )}
    </div>
  );
};

export default FinancialCloseoutInvoiceTable;
